import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [ "map", "attachment" ]

  connect() {
    this.invalidHandler = (event) => this.handleInvalid(event)
    this.element.addEventListener("invalid", this.invalidHandler, true)
  }

  disconnect() {
    this.element.removeEventListener("invalid", this.invalidHandler, true)
  }

  handleInvalid(event) {
    if (this.handlingInvalid) return
    this.handlingInvalid = true

    try {
      event.preventDefault()

      const invalidField = this.findFirstInvalidField(this.element)
      const invalidMap = this.hasMapTarget && !this.mapHasCoordinates() ? this.mapTarget : null
      const invalidAttachment = this.hasAttachmentTarget && !this.attachmentOptional && !this.attachmentHasFiles() ? this.attachmentTarget : null

      const firstError = this.firstInDocument(invalidField, invalidMap, invalidAttachment)
      if (!firstError) return

      const customSelect = this.customSelectFor(invalidField)

      this.scrollToElement(customSelect?.scrollTarget || firstError, () => {
        if (customSelect) {
          customSelect.controller.showError(customSelect.controller.errorMessageValue || "Выберите значение")
          customSelect.controller.triggerTarget.focus({ preventScroll: true })
        } else if (firstError === invalidField) {
          invalidField.focus({ preventScroll: true })
          invalidField.reportValidity()
        } else if (firstError === invalidMap) {
          this.showMapError(this.mapTarget.dataset.formValidationMessage || "Укажите точку на карте")
        } else if (firstError === invalidAttachment) {
          const message = this.attachmentTarget.dataset.formValidationMessage || "Добавьте файл"
          this.showError(this.attachmentTarget, message)
        }
      })
    } finally {
      this.handlingInvalid = false
    }
  }

  submit(event) {
    this.clearCustomErrors()
    if (this.hasMapTarget) this.clearMapError()

    const invalidMap = this.hasMapTarget && !this.mapHasCoordinates() ? this.mapTarget : null
    const invalidAttachment = this.hasAttachmentTarget && !this.attachmentOptional && !this.attachmentHasFiles() ? this.attachmentTarget : null

    const firstError = this.firstInDocument(invalidMap, invalidAttachment)

    if (firstError) {
      event.preventDefault()
      this.scrollToElement(firstError, () => {
        if (firstError === invalidMap) {
          this.showMapError(this.mapTarget.dataset.formValidationMessage || "Укажите точку на карте")
        } else {
          const message = this.attachmentTarget.dataset.formValidationMessage || "Добавьте файл"
          this.showError(this.attachmentTarget, message)
        }
      })
    }
  }

  get attachmentOptional() {
    return this.hasAttachmentTarget && this.attachmentTarget.dataset.formValidationOptional === "true"
  }

  findFirstInvalidField(form) {
    for (const field of form.elements) {
      if (field.willValidate && !field.validity.valid) return field
    }
    return null
  }

  customSelectFor(field) {
    if (!field || field.dataset.selectFieldTarget !== "native") return null

    const wrapper = field.closest('[data-controller~="select-field"]')
    if (!wrapper) return null

    const controller = this.application.getControllerForElementAndIdentifier(wrapper, "select-field")
    if (!controller || !controller.hasTriggerTarget) return null

    return { controller, scrollTarget: controller.triggerTarget }
  }

  firstInDocument(...elements) {
    const present = elements.filter((el) => el)
    if (present.length === 0) return null

    return present.sort((a, b) => {
      const position = a.compareDocumentPosition(b)
      if (position & Node.DOCUMENT_POSITION_FOLLOWING) return -1
      if (position & Node.DOCUMENT_POSITION_PRECEDING) return 1
      return 0
    })[0]
  }

  scrollToElement(element, callback) {
    element.scrollIntoView({ behavior: "smooth", block: "start" })
    setTimeout(() => callback && callback(), 600)
  }

  mapHasCoordinates() {
    const lat = this.mapTarget.querySelector("input[data-yandex-map-form-target='lat']")?.value
    const lng = this.mapTarget.querySelector("input[data-yandex-map-form-target='lng']")?.value
    return lat && lng
  }

  attachmentHasFiles() {
    return this.attachmentTarget.querySelectorAll("input[type='hidden'][data-signed-id]").length > 0
  }

  showError(container, message) {
    let error = container.querySelector("[data-form-validation-error]")
    if (!error) {
      error = document.createElement("p")
      error.className = "text-red-600 text-xs mt-1"
      error.dataset.formValidationError = true
      container.appendChild(error)
    }
    error.textContent = message
    error.classList.remove("hidden")
  }

  showMapError(message) {
    const container = this.mapTarget.querySelector("[data-yandex-map-form-target='container']")
    if (container) {
      container.classList.remove("border-gray-300")
      container.classList.add("border-red-500")
    }

    const display = this.mapTarget.querySelector("[data-yandex-map-form-target='addressDisplay']")
    if (!display) return

    const text = display.querySelector("[data-yandex-map-form-target='addressText']")
    if (text) text.textContent = message

    display.className = "min-h-[1.5rem] text-sm text-red-600 flex items-center gap-1.5"

    const icon = display.querySelector("[data-yandex-map-form-target='addressIcon']")
    if (icon) icon.classList.add("hidden")
  }

  clearMapError() {
    const container = this.mapTarget.querySelector("[data-yandex-map-form-target='container']")
    if (container) {
      container.classList.remove("border-red-500")
      container.classList.add("border-gray-300")
    }
  }

  clearCustomErrors() {
    this.element.querySelectorAll("[data-form-validation-error]").forEach((el) => {
      el.classList.add("hidden")
      el.textContent = ""
    })
  }
}
